import { ProOrganizerRequestEntity } from '../domains/pro-organizer-request';

const INSURANCE_EXPIRED_HISTORY_KEY = 'INSURANCE_EXPIRED';
const RESUBMITTED_HISTORY_KEY = 'RESUBMITTED';
const PENDING_HISTORY_KEY = 'PENDING';
const INSURANCE_RENEWAL_UNDER_REVIEW_STATUS = 'INSURANCE_RENEWAL_UNDER_REVIEW';

function normalizeLoose(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function getLatestHistory(
  request: ProOrganizerRequestEntity,
):
  | { statusSnapshot?: { key?: string; value?: string }; note?: string; updatedAt?: string; createdAt?: string }
  | undefined {
  const histories = request?.histories ?? [];
  if (!histories.length) return undefined;
  return histories[histories.length - 1];
}

function getLatestHistoryKey(request: ProOrganizerRequestEntity): string {
  const latestHistory = getLatestHistory(request);
  return normalizeLoose((latestHistory?.statusSnapshot as { key?: string } | undefined)?.key);
}

export function isInsuranceRenewalTypeRequest(request: ProOrganizerRequestEntity): boolean {
  const latestKey = getLatestHistoryKey(request);
  return (
    latestKey === normalizeLoose(INSURANCE_EXPIRED_HISTORY_KEY) ||
    latestKey === normalizeLoose(RESUBMITTED_HISTORY_KEY) ||
    latestKey === normalizeLoose(PENDING_HISTORY_KEY)
  );
}

function toTimeMs(value: string | undefined): number {
  const ms = new Date(value || 0).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function getRequestRecencyMs(request: ProOrganizerRequestEntity): number {
  const latestHistory = getLatestHistory(request);
  const historyTime = toTimeMs(latestHistory?.updatedAt || latestHistory?.createdAt);
  if (historyTime > 0) return historyTime;

  const typedRequest = request as ProOrganizerRequestEntity & {
    updatedAt?: string;
    createdAt?: string;
  };
  const requestTime = new Date(typedRequest.updatedAt || typedRequest.createdAt || 0).getTime();
  return Number.isFinite(requestTime) ? requestTime : 0;
}

export function pickRenewInsuranceRequest(
  requests: ProOrganizerRequestEntity[] | null | undefined,
): ProOrganizerRequestEntity | undefined {
  const list = [...(requests ?? [])];
  if (!list.length) return undefined;

  list.sort((a, b) => getRequestRecencyMs(b) - getRequestRecencyMs(a));
  return list[0];
}

export function isRenewInsuranceUnderReviewStatus(request: ProOrganizerRequestEntity | undefined): boolean {
  if (!request) return false;
  if (!isInsuranceRenewalTypeRequest(request)) return false;
  return getLatestHistoryKey(request) === normalizeLoose(RESUBMITTED_HISTORY_KEY);
}

export function shouldOpenSubmittedDetailOnRenewAction(request: ProOrganizerRequestEntity | undefined): boolean {
  if (!request) return false;
  if (!isInsuranceRenewalTypeRequest(request)) return false;
  const latestKey = getLatestHistoryKey(request);
  return latestKey === normalizeLoose(PENDING_HISTORY_KEY) || latestKey === normalizeLoose(RESUBMITTED_HISTORY_KEY);
}

export function getRenewInsuranceDetailModalData(request: ProOrganizerRequestEntity): {
  message: string;
  status: string;
  date: string;
} {
  const latestHistory = getLatestHistory(request);
  const latestKey = getLatestHistoryKey(request);
  const value = (latestHistory?.statusSnapshot as { value?: string } | undefined)?.value;
  const status =
    latestKey === normalizeLoose(RESUBMITTED_HISTORY_KEY)
      ? INSURANCE_RENEWAL_UNDER_REVIEW_STATUS
      : latestKey === normalizeLoose(INSURANCE_EXPIRED_HISTORY_KEY)
        ? INSURANCE_EXPIRED_HISTORY_KEY
        : value ?? '';

  return {
    message: latestHistory?.note ?? '',
    status,
    date: latestHistory?.updatedAt || latestHistory?.createdAt || '',
  };
}
